import * as Express from 'express'
import type { GraphApiResponse } from '@adc/sdk/types'
import responseParsers, { ResponseParser } from './responseParsers'
import { GraphVariantOptions, GraphResponse } from './graph'

export { GraphVariantOptions, GraphResponse }

export interface StringIndexable {
  [index: string]: any
}

export type HTTPMethod = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'PUT'

export type ResponseValueType =
  | string
  | ArrayBuffer
  | Blob
  | FormData
  | Record<string, string>
  | GraphResponse<unknown>

// TODO: This is the body format thrown for apps to `catch` later on. It uses
// an interface to add this right now, so we aren't using this type, but we
// may refactor and point this out explicitly as the reject type in the Promise generic type
// we use for the fetcher
//
// export interface ExtendedBody extends Body {
//   url?: string
//   method?: string
// }

export interface PatchedResponse<V = ResponseValueType> {
  body: V
  clone(): Response
  headers: Headers
  ok: boolean
  size: number
  status: number
  statusText: string
  timeout: number
  type: ResponseType
  url: string
}

type FetchEventName = 'start' | 'success' | 'failure'

export interface EventCapture {
  on: (eventName: FetchEventName, ctx: any) => void
}

const EventSinkhole = {
  // @ts-ignore
  on(eventName: FetchEventName, ctx: any) {},
}

export interface FetchOptions {
  method?: HTTPMethod
  headers?: HeadersInit | Record<string, any>
  body?: BodyInit | Record<string, any>
  parseResponse?: ResponseParser
  tries?: number
  retryTimeout?: number
  requestTimeout?: number
  queryParams?: Record<string, any>
  eventCapture?: EventCapture
  controller?: AbortController
}

export type MethodFetchInvocationOptions = Omit<FetchOptions, 'method'>

export type BrowserFetchOptions = FetchOptions & {
  useProxy?: boolean
  credentials?: RequestCredentials
}

export type NodeFetchOptions = FetchOptions & {
  req?: Express.Request
}

export type IsomorphicFetchOptions = NodeFetchOptions | BrowserFetchOptions

export interface AdcFetch {
  (
    url: string,
    options?: NodeFetchOptions & BrowserFetchOptions
  ): Promise<PatchedResponse>

  get: (
    url: string,
    options?: MethodFetchInvocationOptions
  ) => Promise<PatchedResponse>
  put: (
    url: string,
    options?: MethodFetchInvocationOptions
  ) => Promise<PatchedResponse>
  post: (
    url: string,
    options?: MethodFetchInvocationOptions
  ) => Promise<PatchedResponse>
  patch: (
    url: string,
    options?: MethodFetchInvocationOptions
  ) => Promise<PatchedResponse>
  delete: (
    url: string,
    options?: MethodFetchInvocationOptions
  ) => Promise<PatchedResponse>

  fromService: (service: string) => AdcFetch
  forAuctionGraph: <R extends string, T = unknown>(
    graphVariantOptions: GraphVariantOptions,
    opts?: NodeFetchOptions | BrowserFetchOptions
  ) => Promise<PatchedResponse<GraphApiResponse<R, T>>>
  withProgress: (url: string, opts: any) => Promise<any>

  // @compat
  serializeParams: (params: Record<string, string>) => string
  responseParsers: typeof responseParsers
  __internal: Record<string, any>
}

export const defaultFetchOptions = {
  method: 'get',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  // TODO: Make this specific to just browser default options
  // credentials: 'same-origin',
  queryParams: {},
  parseResponse: responseParsers.json,
  tries: 1,
  retryTimeout: 500,
  eventCapture: EventSinkhole,
}

export default AdcFetch
